import { ChevronRight } from "lucide-react";
import { Fragment, useEffect, useRef } from "react";
import { diffWords, markProtectedFacts, type DiffSegment } from "../../lib/diff";

/**
 * LOCAL PRIMITIVES FOR THE EDITOR — and they are local ONLY because ten screens are being
 * rebuilt concurrently and src/components/primitives.tsx is owned by another workstream this
 * round. Every one of these belongs in the shared layer, and the consolidation pass should
 * lift them there verbatim:
 *
 *   AutoGrowTextArea  -> primitives.tsx, beside AutoGrowInput, sharing the useAutoGrow hook.
 *   Disclosure        -> primitives.tsx (the design contract already specifies it).
 *   SectionDiff       -> DocumentDiff with `layout="stacked"`, exactly as the contract says.
 *   ProtectedChips    -> ProtectedFacts, exported from DocumentDiff.tsx.
 *
 * Nothing here declares a colour, a font-size or a length: SectionDiff and ProtectedChips
 * COMPOSE the shared .doc-diff-side / .doc-diff-body / .doc-protected classes from the
 * primitives layer, so the editor's diff and the review diff are painted by literally the same
 * CSS. They cannot drift apart, and no primitive is restyled from a screen layer.
 */

/**
 * Grow a textarea to its content, always.
 *
 * The editor used to size bodies with `rows={body.length > 420 ? 8 : 5}` — a magic character
 * count deciding a layout — so three of four sections were cut mid-sentence on the phone
 * ("without changing the core", "while keeping fu"). A document product may not hide the
 * document. Height is recomputed on every value change and on resize, because a viewport
 * change re-wraps the prose and therefore changes the height it needs.
 */
function useAutoGrow(ref: React.RefObject<HTMLTextAreaElement>, value: string) {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [ref, value]);

  useEffect(() => {
    function resize() {
      const element = ref.current;
      if (!element) return;
      element.style.height = "auto";
      element.style.height = `${element.scrollHeight}px`;
    }
    window.addEventListener("resize", resize);
    // A web font swapping in RE-WRAPS the prose within the SAME width: the section body is set
    // in Source Serif 4, and if it first measured under the fallback face it lands a line short
    // (the exact 170-vs-195 clip on the phone editor). Width never changes, so the width
    // observer below cannot catch it — re-measure the moment the packaged fonts are ready.
    // `document.fonts` is guarded for non-browser test environments.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(resize).catch(() => {});
    }
    // A window `resize` alone is not enough: the control's OWN width can change after mount
    // without the viewport changing — a sibling rail settling, a late layout pass — and each of
    // those re-wraps the prose and changes the height it needs. Measuring only once leaves the
    // box a line short and the text scrolls inside a control whose whole contract is "grow to
    // fit, never scroll". Observe the element's own WIDTH (never height — reacting to our own
    // height mutation would spin a ResizeObserver loop).
    const element = ref.current;
    let observer: ResizeObserver | undefined;
    if (element && typeof ResizeObserver !== "undefined") {
      let lastWidth = element.clientWidth;
      observer = new ResizeObserver(() => {
        const width = ref.current?.clientWidth ?? lastWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        resize();
      });
      observer.observe(element);
    }
    return () => {
      window.removeEventListener("resize", resize);
      observer?.disconnect();
    };
  }, [ref]);
}

/**
 * The multi-line sibling of AutoGrowInput. Newlines are allowed (a prompt edit appends a
 * paragraph), Enter is NOT suppressed, and the control never scrolls internally — it grows.
 */
export function AutoGrowTextArea({
  value,
  onValueChange,
  className,
  ...rest
}: {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "className">) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutoGrow(ref, value);

  return (
    <textarea
      {...rest}
      ref={ref}
      className={`auto-grow-input ${className || ""}`}
      rows={1}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
    />
  );
}

/**
 * A thin RESTYLE-ONLY wrapper over native <details>/<summary>. [open] is the styling hook and
 * the native open attribute is the state machine — there is no JS height animation, because an
 * animated height desynchronises from the attribute the harness reads.
 */
export function Disclosure({
  summary,
  children,
  testId,
  defaultOpen
}: {
  summary: string;
  children: React.ReactNode;
  testId?: string;
  defaultOpen?: boolean;
}) {
  return (
    <details className="editor-disclosure" data-testid={testId} open={defaultOpen}>
      <summary>
        <ChevronRight aria-hidden="true" />
        <span>{summary}</span>
      </summary>
      <div className="editor-disclosure-body">{children}</div>
    </details>
  );
}

/* ---------------------------------------------------------------- PROTECTED FACTS
   Numbers, dates, currencies, citations, URLs and defined terms render as SEALED CHIPS —
   mono, on the locked ground, hairline amber outline. The product's central promise is that
   the machine never silently alters these, and until now the editor — the one screen where an
   operator can actually type over one — showed that promise nowhere. */

export function ProtectedChips({ items, testId }: { items: string[]; testId?: string }) {
  return (
    <>
      {items.map((item) => (
        <mark className="doc-protected" data-testid={testId} title="Protected fact — never altered" key={item}>
          {item}
        </mark>
      ))}
    </>
  );
}

/**
 * The distinct protected tokens in a body, in reading order.
 *
 * NAME THE FACT, NOT THE FRAGMENT. markProtected() tags the NUMBER — "2024", "2026" — but the
 * thing the rail lists and Final QA actually checks is the whole protected fact, "WHO 2024" or
 * "first cohort 2026". A chip that says "2024" names a fragment of the guarded term and reads as
 * a different, weaker promise than the one the product makes. So when a detected token is part of
 * a declared protected fact AND that whole fact is present in this body, the chip carries the
 * fact. The generic token is the fallback, never an invention: nothing is shown that is not in
 * the text. Memoize this on the body string.
 *
 * ONE FILTER, EVERY SURFACE. The fragment rule ("20" and "26" are not facts) used to live only
 * here, in the function that feeds the CHIP ROW, while the prose and the diff still rendered from
 * the raw detector — so the same edit that produced an honest chip row produced two false amber
 * seals inside the After pane. The rule now lives in markProtectedFacts(), which every surface on
 * this screen renders from, so the row, the prose and the diff cannot disagree.
 */
export function protectedTokens(body: string, facts: string[] = []) {
  const seen: string[] = [];
  const add = (value: string) => {
    if (value && !seen.includes(value)) seen.push(value);
  };

  for (const piece of markProtectedFacts(body, facts)) {
    const value = piece.value.trim();
    if (!piece.protected || !value) continue;

    /* The shortest declared fact that CONTAINS this token and is itself in this body. */
    const fact = facts
      .filter((item) => item !== value && item.includes(value) && body.includes(item))
      .sort((a, b) => a.length - b.length)[0];

    add(fact || value);
  }

  /* A fact chip subsumes any fragment chip of the same fact ("WHO 2024" makes "2024" noise). */
  return seen.filter((value) => !seen.some((other) => other !== value && other.includes(value)));
}

/**
 * Seal the protected runs inside a piece of prose, leaving the rest as plain text.
 *
 * `facts` is the document's DECLARED protected facts. A chip is an assertion that Final QA
 * checks this string against the canonical source, so the renderer marks only what it can stand
 * behind — see markProtectedFacts(). Passing no facts is still safe (it merely keeps the richer
 * tokens and drops bare one- and two-digit fragments), but every call on this screen passes them.
 */
export function ProtectedText({ value, facts = [] }: { value: string; facts?: string[] }) {
  return (
    <>
      {markProtectedFacts(value, facts).map((piece, index) =>
        piece.protected ? (
          <mark className="doc-protected" title="Protected fact — never altered" key={index}>
            {piece.value}
          </mark>
        ) : (
          <Fragment key={index}>{piece.value}</Fragment>
        )
      )}
    </>
  );
}

/* ---------------------------------------------------------------------- SECTION DIFF
   The editor finally has a diff. It is the SAME diff: word-level LCS from lib/diff.ts, the
   same <del>/<ins> marks with their "−"/"+" gutter glyphs (colour is never the sole channel),
   the same sealed protected chips, the same Source Serif 4 body at the same measure — because
   it reuses the primitives layer's classes rather than re-improvising them.

   It is STACKED at every viewport: it lives inside a 68ch column, where two columns would be
   ~34ch each and neither would read as prose. */

function DiffRun({
  segments,
  side,
  facts
}: {
  segments: DiffSegment[];
  side: "before" | "after";
  facts: string[];
}) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "equal") return <ProtectedText value={segment.value} facts={facts} key={index} />;
        if (side === "before" && segment.type === "delete") {
          return (
            /* THE MARKER IS SPOKEN, NOT PRINTED. The shared mark carries a "−"/"+" gutter glyph
               so colour is never the sole channel — but inside the DOCUMENT it lands as a literal
               character glued into serif prose ("with a − first cohort"), which reads as debug
               output on the one surface that must read as a page. Here the non-colour channel is
               the strikethrough on <del> and the tinted ground on <ins>, and the word itself is
               announced to a screen reader. The glyph is suppressed for THIS diff only, in the
               editor's own layer; the review diff is untouched. */
            <del key={index}>
              <span className="sr-only">Removed: </span>
              <ProtectedText value={segment.value} facts={facts} />
            </del>
          );
        }
        if (side === "after" && segment.type === "insert") {
          return (
            <ins key={index}>
              <span className="sr-only">Added: </span>
              <ProtectedText value={segment.value} facts={facts} />
            </ins>
          );
        }
        return null;
      })}
    </>
  );
}

export function SectionDiff({
  before,
  after,
  facts = []
}: {
  before: string;
  after: string;
  facts?: string[];
}) {
  const segments = diffWords(before, after);
  return (
    <div className="editor-diff" data-testid="section-diff-body" data-layout="stacked">
      <div className="doc-diff-side" data-side="before">
        <span className="doc-diff-label">Before</span>
        <p className="doc-diff-body" data-testid="section-diff-before">
          <DiffRun segments={segments} side="before" facts={facts} />
        </p>
      </div>
      <div className="doc-diff-side" data-side="after">
        <span className="doc-diff-label">After</span>
        <p className="doc-diff-body" data-testid="section-diff-after">
          <DiffRun segments={segments} side="after" facts={facts} />
        </p>
      </div>
    </div>
  );
}
